import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import { resolveDmaIds } from '@shared/dma-utils';

export interface AudiencePersona {
  id: number;
  brandId?: number;
  profileName: string;
  description?: string;
  ageRanges?: string[];
  genders?: string[];
  ethnicities?: string[];
  incomeLevels?: string[];
  targetLocations?: string[];  // DMA names (stored in DB)
  targetDmaIds?: number[];      // DMA IDs (resolved at runtime from targetLocations)
  interests?: string[];
  musicGenres?: string[];
  lifestyleAttributes?: string[];
  // Track which values are required (hard filters)
  requiredAgeRanges?: string[];
  requiredGenders?: string[];
  requiredEthnicities?: string[];
  requiredIncomeLevels?: string[];
  requiredTargetLocations?: string[];
  requiredLifestyleAttributes?: string[];
  requiredMusicGenres?: string[];
  isQuickAccess?: boolean;
  lastUsed?: Date;
  color?: string;
  minMatchPercentage?: number;
}

interface PersonaContextType {
  activePersona: AudiencePersona | null;
  setActivePersona: (persona: AudiencePersona | null) => void;
  savedPersonas: AudiencePersona[];
  isLoading: boolean;
  createPersona: (data: Partial<AudiencePersona>) => Promise<AudiencePersona>;
  updatePersona: (id: number, data: Partial<AudiencePersona>) => Promise<void>;
  deletePersona: (id: number) => Promise<void>;
  clearActivePersona: () => void;
  updateMinMatchPercentage: (percentage: number) => void;
}

const PersonaContext = createContext<PersonaContextType | null>(null);

export const usePersona = () => {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within PersonaProvider');
  }
  return context;
};

const ACTIVE_PERSONA_KEY = 'musicsponsor_active_persona';

export const PersonaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activePersona, setActivePersonaState] = useState<AudiencePersona | null>(null);
  const isUpdatingRef = useRef(false);

  // Fetch all saved personas for the user
  const { data: brandsWithProfiles = [], isLoading } = useQuery({
    queryKey: ['/api/brands'],
    enabled: !!user,
    queryFn: async () => {
      const response = await fetch('/api/brands', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        return [];
      }
      
      return response.json();
    },
  });

  // Flatten all audience profiles from all brands
  const savedPersonas: AudiencePersona[] = brandsWithProfiles.flatMap((brand: any) => 
    (brand.audienceProfiles || []).map((profile: any) => ({
      ...profile,
      brandId: brand.id,
      brandName: brand.name,
      minMatchPercentage: profile.minMatchPercentage ?? 0, // Ensure default value
    }))
  );

  // Load active persona from localStorage on mount
  useEffect(() => {
    if (!user) {
      setActivePersonaState(null);
      return;
    }

    // Don't reload if we're in the middle of updating
    if (isUpdatingRef.current) {
      return;
    }

    // Wait for savedPersonas to load from API before validating
    // This prevents a race condition where we'd remove the persona from localStorage
    // before the API finishes loading
    if (isLoading) {
      return;
    }

    const stored = localStorage.getItem(ACTIVE_PERSONA_KEY);
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);

      // Quick templates don't exist in savedPersonas but should still be valid
      if (parsed.isQuickTemplate) {
        // Resolve DMA IDs asynchronously for quick templates
        resolvePersonaDmaIds(parsed).then(resolvedPersona => {
          setActivePersonaState(resolvedPersona);
        }).catch(error => {
          console.error('Failed to resolve DMA IDs on load:', error);
          // Still set the persona even if DMA resolution fails
          setActivePersonaState(parsed);
        });
        return;
      }

      // Verify the persona still exists in saved personas
      const exists = savedPersonas.find(p => p.id === parsed.id);

      if (exists) {
        // Merge stored data with API data, prioritizing stored minMatchPercentage
        const merged = {
          ...exists,
          minMatchPercentage: parsed.minMatchPercentage ?? exists.minMatchPercentage ?? 0
        };

        // Resolve DMA IDs asynchronously
        resolvePersonaDmaIds(merged).then(resolvedPersona => {
          setActivePersonaState(resolvedPersona);
        }).catch(error => {
          console.error('Failed to resolve DMA IDs on load:', error);
          // Still set the persona even if DMA resolution fails
          setActivePersonaState(merged);
        });
      } else {
        // Persona no longer exists, remove from localStorage
        localStorage.removeItem(ACTIVE_PERSONA_KEY);
      }
    } catch (error) {
      console.error('Failed to load active persona:', error);
      localStorage.removeItem(ACTIVE_PERSONA_KEY);
    }
  }, [user, savedPersonas.length, isLoading]);

  // Helper: Resolve DMA names to IDs
  const resolvePersonaDmaIds = async (persona: AudiencePersona): Promise<AudiencePersona> => {
    if (!persona.targetLocations || persona.targetLocations.length === 0) {
      return { ...persona, targetDmaIds: [] };
    }

    try {
      const dmaIds = await resolveDmaIds(persona.targetLocations);
      return { ...persona, targetDmaIds: dmaIds };
    } catch (error) {
      console.error('Failed to resolve DMA IDs for persona:', error);
      // Return persona without DMA IDs on error
      return { ...persona, targetDmaIds: [] };
    }
  };

  // Set active persona and persist to localStorage
  const setActivePersona = useCallback(async (persona: AudiencePersona | null) => {
    if (!persona) {
      setActivePersonaState(null);
      localStorage.removeItem(ACTIVE_PERSONA_KEY);
      // Invalidate queries when clearing persona
      queryClient.invalidateQueries({ queryKey: ['/api/artists-with-shows/paginated'] });
      queryClient.invalidateQueries({ queryKey: ['/api/venues-with-shows/paginated'] });
      queryClient.invalidateQueries({ queryKey: ['/api/markets-with-shows/paginated'] });
      queryClient.invalidateQueries({ queryKey: ['/api/shows'] });
      return;
    }

    // Resolve DMA names to IDs
    const resolvedPersona = await resolvePersonaDmaIds(persona);

    setActivePersonaState(resolvedPersona);
    localStorage.setItem(ACTIVE_PERSONA_KEY, JSON.stringify(resolvedPersona));

    // Invalidate queries when setting persona to force fresh data with new filters
    queryClient.invalidateQueries({ queryKey: ['/api/artists-with-shows/paginated'] });
    queryClient.invalidateQueries({ queryKey: ['/api/venues-with-shows/paginated'] });
    queryClient.invalidateQueries({ queryKey: ['/api/markets-with-shows/paginated'] });
    queryClient.invalidateQueries({ queryKey: ['/api/shows'] });

    // Update last used timestamp - but only for real personas, not quick templates
    if (resolvedPersona.id && !(resolvedPersona as any).isQuickTemplate && typeof resolvedPersona.id === 'number') {
      updatePersonaMutation.mutate({
        id: resolvedPersona.id,
        data: { lastUsed: new Date() }
      });
    }

    // Notify user
    toast({
      title: 'Persona Applied',
      description: `Now filtering with "${resolvedPersona.profileName}" persona${resolvedPersona.targetDmaIds && resolvedPersona.targetDmaIds.length > 0 ? ` (${resolvedPersona.targetDmaIds.length} market${resolvedPersona.targetDmaIds.length !== 1 ? 's' : ''})` : ''}`,
    });
  }, [toast, queryClient]);

  const clearActivePersona = useCallback(() => {
    setActivePersona(null);
    toast({
      title: 'Persona Cleared',
      description: 'Showing all results without persona filtering',
    });
  }, [setActivePersona, toast]);

  // Create persona mutation
  const createPersonaMutation = useMutation({
    mutationFn: async (data: Partial<AudiencePersona>) => {
      // First, ensure user has at least one brand
      let brandId = data.brandId;
      
      if (!brandId && brandsWithProfiles.length === 0) {
        // Create a default brand for the user
        const brandResponse = await apiRequest('/api/brands', {
          method: 'POST',
          body: { name: user?.company || 'My Brand' },
        });
        brandId = brandResponse.id;
      } else if (!brandId && brandsWithProfiles.length === 1) {
        // If only one brand exists and no brandId specified, use it
        brandId = brandsWithProfiles[0].id;
      } else if (!brandId && brandsWithProfiles.length > 1) {
        // Multiple brands exist but no brandId specified - this should be prevented by UI
        throw new Error('Please select a brand for this persona');
      }

      // Create the audience profile
      const response = await apiRequest('/api/audience-profiles', {
        method: 'POST',
        body: {
          ...data,
          brandId,
          isQuickAccess: true,
          minMatchPercentage: data.minMatchPercentage || 0, // Set default to 0
        },
      });
      
      return response;
    },
    onSuccess: (newPersona) => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({
        title: 'Persona Created',
        description: `"${newPersona.profileName}" has been saved to your profile`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error Creating Persona',
        description: error.message || 'Failed to create persona',
        variant: 'destructive',
      });
    },
  });

  // Update persona mutation
  const updatePersonaMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<AudiencePersona> }) => {
      await apiRequest(`/api/audience-profiles/${id}`, {
        method: 'PATCH',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
    },
  });

  // Delete persona mutation
  const deletePersonaMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest(`/api/audience-profiles/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({
        title: 'Persona Deleted',
        description: 'The persona has been removed from your profile',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error Deleting Persona',
        description: error.message || 'Failed to delete persona',
        variant: 'destructive',
      });
    },
  });

  const createPersona = async (data: Partial<AudiencePersona>) => {
    return createPersonaMutation.mutateAsync(data);
  };

  const updatePersona = async (id: number, data: Partial<AudiencePersona>) => {
    console.log('updatePersona called with id:', id, 'data:', data);
    await updatePersonaMutation.mutateAsync({ id, data });
    console.log('updatePersona mutation completed');

    // Update active persona if it's the one being updated
    if (activePersona?.id === id) {
      const updated = { ...activePersona, ...data };
      console.log('Updating active persona with:', updated);

      // Re-resolve DMA IDs if targetLocations changed
      if (data.targetLocations) {
        await setActivePersona(updated);
      } else {
        setActivePersonaState(updated);
        localStorage.setItem(ACTIVE_PERSONA_KEY, JSON.stringify(updated));
      }
    }
  };

  const deletePersona = async (id: number) => {
    // Clear active persona if it's being deleted
    if (activePersona?.id === id) {
      clearActivePersona();
    }
    
    await deletePersonaMutation.mutateAsync(id);
  };

  const updateMinMatchPercentage = useCallback((percentage: number) => {
    isUpdatingRef.current = true;
    
    setActivePersonaState(current => {
      if (current) {
        const updated = { ...current, minMatchPercentage: percentage };
        localStorage.setItem(ACTIVE_PERSONA_KEY, JSON.stringify(updated));
        
        // Reset the updating flag after a short delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 100);
        
        return updated;
      }
      isUpdatingRef.current = false;
      return current;
    });
  }, []);

  return (
    <PersonaContext.Provider
      value={{
        activePersona,
        setActivePersona,
        savedPersonas,
        isLoading,
        createPersona,
        updatePersona,
        deletePersona,
        clearActivePersona,
        updateMinMatchPercentage,
      }}
    >
      {children}
    </PersonaContext.Provider>
  );
};